import React, { CSSProperties, FC, useEffect, useMemo, useState } from 'react';

import { ProductData, StoreRefreshPopup } from '@appcharge/shared-ui';

import useOfferDesign from 'api/useOfferDesign';
import { EmblemType, RibbonType } from 'common/contracts';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';
import { IResolvedProgressBarMission } from 'views/Offers/utils/resolveProgressBarPoints';
import { POPUP_CONTAINER_STYLE } from 'views/OfferDesign/pages/OfferDesignForm/components/OfferDesignPreview/OfferDesignPreview.consts';

import { BadgeOverlay } from './BadgeOverlay';

const PREVIEW_CONTAINER_ID = 'popup-preview';
const MAX_PREVIEW_PRODUCTS = 4;

const PREVIEW_CONTAINER_STYLE: CSSProperties = {
  ...POPUP_CONTAINER_STYLE,
  width: '100%'
};

interface ITriggeredPopupPreviewProps {
  publisherId?: string;
  currentOfferDesign: string;
  currentPrice: number | null;
  previewProducts: ProductData[];
  progressBarPoints?: IResolvedProgressBarMission[];
  emblemBadges?: EmblemType[];
  ribbonBadges?: RibbonType[];
}

export const TriggeredPopupPreview: FC<ITriggeredPopupPreviewProps> = ({
  publisherId,
  currentOfferDesign,
  currentPrice,
  previewProducts,
  progressBarPoints,
  emblemBadges,
  ribbonBadges
}): React.ReactElement => {
  const { getOfferDesign } = useOfferDesign(publisherId, currentOfferDesign);
  const offerData = getOfferDesign?.data;

  const cappedPreviewProducts = useMemo<ProductData[]>(
    () => previewProducts.slice(0, MAX_PREVIEW_PRODUCTS),
    [previewProducts]
  );

  const safePrice =
    currentPrice === null ||
    currentPrice === undefined ||
    Number.isNaN(currentPrice)
      ? 0
      : currentPrice;

  let buttonText = `$${safePrice}`;
  if (safePrice === 0) {
    buttonText = 'Collect';
  }

  const selectedRibbon = ribbonBadges?.[0];

  return (
    <OfferPreviewShell
      containerId={PREVIEW_CONTAINER_ID}
      containerStyle={PREVIEW_CONTAINER_STYLE}
    >
      {() => (
        <React.Fragment>
          <PortaledStoreRefreshPopup
            containerId={PREVIEW_CONTAINER_ID}
            title={offerData?.specialOffer?.title || ''}
            titleColor={offerData?.specialOffer?.fontColor?.colorOne}
            titleWeight={offerData?.specialOffer?.fontWeight}
            backgroundColor={offerData?.specialOffer?.backgroundColor?.colorOne}
            backgroundImage={offerData?.backgroundImage}
            borderColor={offerData?.borderColor}
            borderWidth={offerData?.borderWidth}
            buttonText={buttonText}
            products={cappedPreviewProducts}
            progressBarPoints={progressBarPoints}
            emblemBadges={emblemBadges}
          />
          <BadgeOverlay ribbon={selectedRibbon} />
        </React.Fragment>
      )}
    </OfferPreviewShell>
  );
};

interface IPortaledStoreRefreshPopupProps {
  containerId: string;
  title: string;
  titleColor?: string;
  titleWeight?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  borderColor?: React.ComponentProps<typeof StoreRefreshPopup>['borderColor'];
  borderWidth?: number;
  buttonText: string;
  products: ProductData[];
  progressBarPoints?: IResolvedProgressBarMission[];
  emblemBadges?: EmblemType[];
}

const PortaledStoreRefreshPopup: FC<IPortaledStoreRefreshPopupProps> = ({
  containerId,
  title,
  titleColor,
  titleWeight,
  backgroundColor,
  backgroundImage,
  borderColor,
  borderWidth,
  buttonText,
  products,
  progressBarPoints,
  emblemBadges
}): React.ReactElement | null => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setContainer(document.getElementById(containerId));
  }, [containerId]);

  if (!container) {
    return null;
  }

  return (
    <StoreRefreshPopup
      open
      disableBodyLock
      disableShadow
      onClose={() => {}}
      onCollect={() => {}}
      title={title}
      titleColor={titleColor}
      titleWeight={titleWeight}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      borderColor={borderColor}
      borderWidth={borderWidth}
      buttonText={buttonText}
      products={products}
      container={container}
      progressBarPoints={progressBarPoints}
      emblemBadges={emblemBadges}
    />
  );
};
